<div [formGroup]="mappingForm" class="mapping-editor">
  <header class="mapping-header">
    <div>
      <h3>Standard field mapping</h3>
      <p>
        Map source JSON paths to the public product fields used by ingestion and product discovery. An empty mapping
        uses the server defaults shown below.
      </p>
    </div>
    <button type="button" mat-raised-button color="primary" (click)="addField()"
            [disabled]="fields.length >= maximumFields">
      <mat-icon>add</mat-icon>
      Field
    </button>
  </header>

  @if (fields.hasError('duplicateKeys')) {
    <p class="mapping-error" role="alert">Each output field name must be unique.</p>
  }
  @if (fields.hasError('maximumItems')) {
    <p class="mapping-error" role="alert">A field mapping can contain at most 64 output fields.</p>
  }

  @if (fields.length === 0) {
    <section class="default-mapping" data-testid="default-field-mapping">
      <div class="default-mapping__header">
        <div>
          <strong>Using built-in defaults</strong>
          <p>These values come from the backend transformer contract.</p>
        </div>
        <button type="button" mat-stroked-button (click)="useEditableDefaults()"
                [disabled]="defaultEntries.length === 0">
          Make defaults editable
        </button>
      </div>
      @if (defaultEntries.length > 0) {
        <dl>
          @for (entry of defaultEntries; track entry.outputName) {
            <div>
              <dt>{{ entry.outputName }}</dt>
              <dd>{{ propertySummary(entry.properties) }}</dd>
            </div>
          }
        </dl>
      } @else {
        <p>Default mapping is unavailable. Reload before changing this feed.</p>
      }
    </section>
  } @else {
    <div formArrayName="fields" class="mapping-fields">
      @for (field of fields.controls; track field; let fieldIndex = $index) {
        <mat-expansion-panel [expanded]="fieldIndex === 0">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ field.get('outputName')?.value || 'New output field' }}</mat-panel-title>
            <mat-panel-description>{{ properties(field).length }} mapping value(s)</mat-panel-description>
          </mat-expansion-panel-header>
          <div [formGroupName]="fieldIndex" class="mapping-field">
            <div class="mapping-row mapping-row--field">
              <mat-form-field appearance="outline">
                <mat-label>Output field</mat-label>
                <input matInput formControlName="outputName" placeholder="price">
                @if (field.get('outputName')?.invalid) {
                  <mat-error>Enter a unique field name (maximum 64 characters).</mat-error>
                }
              </mat-form-field>
              <button type="button" mat-icon-button color="warn" (click)="removeField(fieldIndex)"
                      aria-label="Remove output field">
                <mat-icon>delete</mat-icon>
              </button>
            </div>

            <div formArrayName="properties" class="mapping-properties">
              @if (properties(field).hasError('duplicateKeys')) {
                <p class="mapping-error" role="alert">Property names must be unique within this output field.</p>
              }
              @if (properties(field).hasError('maximumItems')) {
                <p class="mapping-error" role="alert">An output field can contain at most 16 properties.</p>
              }
              @if (properties(field).hasError('minimumItems')) {
                <p class="mapping-error" role="alert">Each output field requires at least one property.</p>
              }
              @for (property of properties(field).controls; track property; let propertyIndex = $index) {
                <div [formGroupName]="propertyIndex" class="mapping-row">
                  <mat-form-field appearance="outline">
                    <mat-label>Property</mat-label>
                    <input matInput formControlName="key" placeholder="path">
                    @if (property.get('key')?.invalid) {
                      <mat-error>Property is required (maximum 64 characters).</mat-error>
                    }
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Value</mat-label>
                    <input matInput formControlName="value" placeholder="$.offers[0].price">
                    @if (property.get('value')?.invalid) {
                      <mat-error>Value is required (maximum 2048 characters).</mat-error>
                    }
                  </mat-form-field>
                  <button type="button" mat-icon-button color="warn"
                          (click)="removeProperty(fieldIndex, propertyIndex)"
                          [disabled]="properties(field).length <= 1"
                          aria-label="Remove mapping property">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              }
              @if (properties(field).length === 1) {
                <p class="mapping-guidance">Each output field must keep at least one property.</p>
              }
            </div>

            <button type="button" mat-stroked-button (click)="addProperty(fieldIndex)"
                    [disabled]="properties(field).length >= maximumProperties">
              <mat-icon>add</mat-icon>
              Property
            </button>
          </div>
        </mat-expansion-panel>
      }
    </div>
  }
</div>
